import { Banner, Box, Card, Page, Spinner, Tabs } from '@shopify/polaris';
import { useMemo, useState } from 'react';
import { useCurrency } from '../../../customHooks/useCurrency';
import { useTimezone } from '../../../customHooks/useTimezone';
import { VerticalSpace } from '../../UI/Reports/VerticalSpace';
import { DateTime } from 'luxon';
import { DIMENSION_BY_KEY, MEASURE_BY_KEY, PRESETS, matchPreset } from './stocks/constants';
import { dateColumnLabels, makeFormatters, shortDay } from './stocks/formatters';
import { PivotTable } from './stocks/PivotTable';
import { PivotToolbar } from './stocks/PivotToolbar';
import { INK_SOFT, LINE } from './stocks/pivotStyles';
import { StocksCharts } from './stocks/StocksCharts';
import { StocksExportMenu } from './stocks/StocksExport';
import { StocksFiltersCard } from './stocks/StocksFiltersCard';
import { StocksKpis } from './stocks/StocksKpis';
import { useStocksData } from './stocks/useStocksData';
import { useStocksFilters } from './stocks/useStocksFilters';
import { useStocksPivotConfig } from './stocks/useStocksPivotConfig';

const PIN_KEY = 'mirtomex.stocks.pinTotal';
const readPin = () => {
  try {
    return window.localStorage.getItem(PIN_KEY) !== 'false';
  } catch (_) {
    return true;
  }
};

const CUSTOM_TAB = { id: 'custom', content: 'Personalizada' };

/**
 * Valoración de existencias: tabla dinámica sobre la cuenta 350.
 *
 * Composición (dirección A del .pen):
 *   - `useStocksFilters`     fechas, ámbito y catálogo (URL).
 *   - `useStocksPivotConfig` filas, columnas, medidas, orden y escala (URL).
 *   - `useStocksData`        consulta; conserva el informe anterior mientras carga.
 *   - Tarjeta de fechas y filtros, franja de KPIs, tarjeta de tabla (vistas
 *     como Tabs, banda de pautado, tabla pivot, notas) y gráficas.
 *
 * El front no recalcula: pinta el árbol que devuelve el back.
 */
const ViewReportStocks = () => {
  const { timezone } = useTimezone();
  const { currency } = useCurrency();
  const filters = useStocksFilters(timezone);
  const config = useStocksPivotConfig();
  const { report, pivot, loading, error } = useStocksData(filters, config);

  const [pinTotal, setPinTotal] = useState(readPin);
  const onPinTotal = (v) => {
    setPinTotal(v);
    try {
      window.localStorage.setItem(PIN_KEY, String(v));
    } catch (_) {
      /* sin localStorage: queda en memoria */
    }
  };
  const [chartMeasure, setChartMeasure] = useState('pvf');

  const fmt = useMemo(() => makeFormatters(currency, config.moneyScale), [currency, config.moneyScale]);
  const todayYmd = DateTime.now().setZone(timezone).toFormat('yyyy-MM-dd');

  // La tabla pinta lo que el back resolvió, no lo que hay en la barra: si se
  // cambia el pautado y aún no ha llegado el dato, se sigue viendo el anterior
  // (atenuado) con sus dimensiones, no una mezcla.
  const shown = pivot?.config || { rows: config.rows, cols: config.cols, compare: filters.hasCompare };
  const shownCompare = Boolean(shown.compare);
  const dateLabels = dateColumnLabels(filters.date, filters.compareDate, todayYmd);

  const subtitle = `${filters.date ? `A ${shortDay(filters.date)}` : `Saldo actual, ${shortDay(todayYmd)}`}${
    filters.compareDate ? `, comparada con ${shortDay(filters.compareDate)}` : ''
  } · importes en ${currency.symbol}`;

  // ---- Vistas predefinidas como Tabs; "Personalizada" si el pautado no coincide.
  const active = matchPreset(config.rows, config.cols);
  const tabs = [...PRESETS.map((p) => ({ id: p.key, content: p.label })), ...(active ? [] : [CUSTOM_TAB])];
  const selectedTab = active ? PRESETS.findIndex((p) => p.key === active.key) : tabs.length - 1;

  // ---- Exportación
  const exportBase = useMemo(
    () => ({
      ...filters.dateParams,
      ...filters.scopeParams,
      ...filters.validFilters,
      ...config.configParams,
      currency: currency.symbol,
      exchange: currency.exchange,
      ...(timezone ? { tz: timezone } : {}),
    }),
    [filters.dateParams, filters.scopeParams, filters.validFilters, config.configParams, currency, timezone],
  );
  const exportDefaults = useMemo(() => {
    const datePart = filters.date || 'actual';
    const vs = filters.compareDate ? `_vs_${filters.compareDate}` : '';
    const sub = `${filters.date ? `A ${shortDay(filters.date)}` : `Saldo a ${shortDay(todayYmd)}`}${
      filters.compareDate ? `, comparada con ${shortDay(filters.compareDate)}` : ''
    }`;
    return {
      fileStem: `existencias_{slug}_${datePart}${vs}`,
      title: 'Valoración de existencias',
      viewTitle: active ? `Valoración de existencias ${active.label.toLowerCase()}` : 'Valoración de existencias',
      subtitle: sub,
    };
  }, [filters.date, filters.compareDate, todayYmd, active]);

  const familiesShown = config.measures.includes('families');

  return (
    <Page
      fullWidth
      title="Valoración de existencias"
      subtitle={subtitle}
      primaryAction={
        <StocksExportMenu
          disabled={!pivot}
          base={exportBase}
          defaults={exportDefaults}
          hasCompare={filters.hasCompare}
          moneyScale={config.moneyScale}
          measures={config.measures}
          reprOn={config.reprOn}
          reprMeasure={config.reprMeasure}
        />
      }
    >
      {!filters.ready ? (
        <Card>
          <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
            <Spinner accessibilityLabel="Cargando filtros" />
          </div>
        </Card>
      ) : (
        <>
          <StocksFiltersCard f={filters} timezone={timezone} />
          <Box minHeight="1rem" />

          {error && (
            <>
              <Banner status="critical" title="No se ha podido cargar el informe">
                <p>{error}</p>
              </Banner>
              <Box minHeight="1rem" />
            </>
          )}

          <div style={{ opacity: loading && pivot ? 0.55 : 1, transition: 'opacity 120ms' }}>
            <StocksKpis root={pivot?.rows} fmt={fmt} compareLabel={shownCompare ? shortDay(filters.compareDate) : ''} />
          </div>
          <Box minHeight="1rem" />

          {/* `stk-card`: la Card recorta con `overflow: clip` y no `hidden`, o la
              cabecera de la tabla no podría pegarse al viewport (ver PivotTable). */}
          <div className="stk-card">
            <Card padding="0">
              <Tabs
                tabs={tabs}
                selected={selectedTab}
                onSelect={(i) => {
                  const t = tabs[i];
                  if (t && t.id !== 'custom') config.applyPreset(t.id);
                }}
              />
              <PivotToolbar config={config} pinTotal={pinTotal} onPinTotal={onPinTotal} />
              {pivot ? (
                <PivotTable
                  pivot={pivot}
                  rowDims={shown.rows}
                  colDims={shown.cols}
                  measures={config.measures}
                  compare={shownCompare}
                  fmt={fmt}
                  dateLabels={dateLabels}
                  moneyScale={config.moneyScale}
                  pinTotal={pinTotal}
                  sortBy={config.sortExplicit ? config.sortBy : null}
                  sortDir={config.sortDir}
                  onSort={config.handleSort}
                  loading={loading}
                  repr={config.repr}
                />
              ) : (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: 10,
                    padding: 48,
                    color: INK_SOFT,
                  }}
                >
                  {loading && <Spinner size="small" accessibilityLabel="Calculando" />}
                  {loading ? 'Calculando la valoración…' : 'Sin datos.'}
                </div>
              )}
              <div
                style={{
                  padding: '10px 16px',
                  borderTop: `1px solid ${LINE}`,
                  fontSize: 12,
                  color: INK_SOFT,
                  lineHeight: '18px',
                }}
              >
                {shownCompare && (
                  <div>
                    {`Δ %: variación sobre el ${shortDay(filters.compareDate)}. «nueva»: sin stock en la fecha de comparación. «sin base»: el saldo de comparación era negativo. En los dos casos no hay porcentaje que calcular. Los precios son los de hoy también para la comparación: se compara volumen, no revalorización.`}
                  </div>
                )}
                <div>
                  {`Importes ${
                    fmt.scale.key === 'full' ? 'completos' : `en ${fmt.scale.suffix} (${fmt.scale.key}${fmt.symbol})`
                  } de ${currency.symbol}${
                    currency.symbol !== 'MXN' ? ' al tipo de cambio actual' : ''
                  }. PVF sin IVA; PVP con IVA.`}
                  {shown.cols.length > 1 && ' Cada grupo de más de una columna cierra con su «Total».'}
                </div>
                {familiesShown && (
                  <div>¹ Serie-color cuenta series distintas: un TOTAL no es la suma de su columna ni de su fila.</div>
                )}
                {config.repr && (
                  <div>
                    {`«% ${MEASURE_BY_KEY[config.repr].label}»: lo que representa cada valor sobre el TOTAL de su columna (la fila TOTAL es el 100 %); en la columna TOTAL, sobre el gran total.${
                      config.repr === 'families'
                        ? ' En serie-color los porcentajes no suman 100: una misma serie cuenta en varias filas.'
                        : ''
                    }${shownCompare ? ' Con comparativa, el % es de la fecha de valoración.' : ''}`}
                  </div>
                )}
                {(shown.rows.includes('year') || shown.cols.includes('year')) && (
                  <div>Año: año de catálogo del producto. Los básicos no tienen año y van aparte, en «Básico».</div>
                )}
                {shown.rows.includes('deposit') || shown.cols.includes('deposit') ? null : (
                  <div>{`Ámbito: ${filters.scopeParams.deposits || filters.scopeParams.depositRoles || filters.scopeParams.depositGroups ? 'los depósitos filtrados' : 'todos los depósitos operables, central incluido'}.`}</div>
                )}
              </div>
            </Card>
          </div>
          <Box minHeight="1rem" />

          {pivot && (
            <StocksCharts
              pivot={pivot}
              measure={chartMeasure}
              onMeasure={setChartMeasure}
              fmt={fmt}
              rowDims={shown.rows}
              colDims={shown.cols}
              compare={shownCompare}
              compareLabel={shortDay(filters.compareDate)}
            />
          )}
          {report && pivot?.rows && !shown.rows.length && (
            <Box paddingBlockStart="2">
              <span style={{ fontSize: 12, color: INK_SOFT }}>
                {`Sin dimensiones en filas: solo se muestra el TOTAL. Añade ${DIMENSION_BY_KEY.deposit.label.toLowerCase()} u otra dimensión en Filas.`}
              </span>
            </Box>
          )}
          <VerticalSpace size="xx-large" />
        </>
      )}
    </Page>
  );
};

export default ViewReportStocks;
